import { Link, Navigate, useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type { NostrMetadata } from '@nostrify/nostrify';

import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { PaytoButtons } from '@/components/stashstr/PaytoButtons';
import { BackButton } from '@/components/stashstr/BackButton';
import { useAuthor } from '@/hooks/useAuthor';
import NotFound from './NotFound';
import { RELAYS, sanitizeUrl } from '@/lib/stashstr';

/**
 * Root-level NIP-19 router:
 * - npub / nprofile -> profile/bio view (avatar, name, nip05, about, payto)
 * - note / nevent   -> redirect to /items/<nevent>
 * - naddr / unknown -> themed dead end (addressable events not supported yet)
 */
export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) {
    return <NotFound />;
  }

  let decoded;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  const { type } = decoded;

  switch (type) {
    case 'npub':
      return <ProfileView pubkey={decoded.data} />;
    case 'nprofile':
      return <ProfileView pubkey={decoded.data.pubkey} />;
    case 'note':
      return <Navigate to={`/items/${noteToNevent(decoded.data)}`} replace />;
    case 'nevent':
      return <Navigate to={`/items/${neventToPath(decoded.data)}`} replace />;
    default:
      return <Nip19DeadEnd label="Addressable events aren't supported yet." />;
  }
}

/** note has no author/relay hints — re-encode as bare nevent. */
function noteToNevent(id: string): string {
  return nip19.neventEncode({ id, relays: RELAYS });
}

/** Re-encode nevent with our relay hints so ItemPage gets author + relays. */
function neventToPath(data: { id: string; author?: string }): string {
  return nip19.neventEncode({
    id: data.id,
    relays: RELAYS,
    ...(data.author ? { author: data.author } : {}),
  });
}

function Nip19DeadEnd({ label }: { label: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 text-center text-muted-foreground">
        <div className="mb-2">
          <BackButton />
        </div>
        {label}{' '}
        <Link to="/" className="text-[#1a56db] dark:text-[#6ea8fe] hover:underline">
          frontpage
        </Link>
      </CardContent>
    </Card>
  );
}

/** Bio-only profile: avatar, name, nip05, about, payto targets. */
function ProfileView({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata: NostrMetadata | undefined = author.data?.metadata;
  const displayName = metadata?.display_name || metadata?.name || pubkey.slice(0, 8);

  return (
    <div className="space-y-4">
      <div>
        <BackButton />
        <Card className="bg-white dark:bg-[#111214] border-gray-200 dark:border-[#2a2d30]">
          <CardContent className="py-5 flex items-start gap-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={sanitizeUrl(metadata?.picture)} alt="" />
            <AvatarFallback className="text-lg bg-gray-100 dark:bg-[#232528] text-gray-600 dark:text-gray-300">
              {displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h1 className="text-[18px] font-bold text-gray-900 dark:text-gray-100">
              {displayName}
            </h1>
            {metadata?.nip05 && (
              <p className="text-[12px] text-gray-500 dark:text-gray-400">
                {metadata.nip05}
              </p>
            )}
            {metadata?.about && (
              <p className="text-[13px] text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap break-words">
                {metadata.about}
              </p>
            )}
          </div>
          </CardContent>
        </Card>
      </div>

      <PaytoButtons pubkey={pubkey} />
    </div>
  );
}
